/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { Suggestion } from 'types/common';

interface SuggestionPanelProps {
  suggestions: Suggestion[];
}

/**
 * Component for displaying teaching guidance card - Simplified
 */
const TeachingGuidanceCard: React.FC<{
  suggestion: Suggestion;
  index: number;
}> = ({ suggestion, index }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 150);
    return () => clearTimeout(timer);
  }, [index]);

  const cardStyle: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
    transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
    position: 'relative',
    overflow: 'visible',
  };

  const teachingGuidance = (suggestion.suggestionsMetadata as any)?.teachingGuidance;

  if (!teachingGuidance) {
    return null;
  }

  return (
    <div className='teaching-guidance-card' style={cardStyle}>
      <div className='guidance-content'>
        <div className='guidance-text'>
          AI is suggesting the tutor to focus on : {teachingGuidance.whatToFocus}
        </div>
      </div>

      <div className='guidance-detail-item'>
        <div className='guidance-detail-text'>
          AI asked the tutor to do this : {teachingGuidance.whyToFocus}
        </div>
      </div>
    </div>
  );
};

/**
 * Component for displaying regular suggestion card
 */
const SuggestionCard: React.FC<{ suggestion: Suggestion; index: number }> = ({
  suggestion,
  index,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), index * 150);
    return () => clearTimeout(timer);
  }, [index]);

  const formatMessage = (message: string): React.ReactNode => {
    if (!message) return '';
    const lines = message.split(/\\n|\n/);
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const cardStyle: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
    transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
    position: 'relative',
    overflow: 'visible',
  };

  return (
    <div className={`suggestion-card ${suggestion.suggestionType}`} style={cardStyle}>
      <div className='suggestion-header-compact'>
        <h3 className='suggestion-title'>{suggestion.title || 'Suggestion'}</h3>
      </div>
      <div className='suggestion-message'>{formatMessage(suggestion.message)}</div>
    </div>
  );
};

/**
 * Main component for displaying suggestions and teaching guidance
 */
const SuggestionPanel: React.FC<SuggestionPanelProps> = ({ suggestions }) => {
  const prevSuggestionsRef = useRef<string[]>([]);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    // Check if new suggestions were added
    const currentIds = suggestions.map((s) => s.id);
    const prevIds = prevSuggestionsRef.current;

    if (currentIds.length > prevIds.length || !currentIds.every((id, i) => id === prevIds[i])) {
      setAnimationKey((prev) => prev + 1);
    }

    prevSuggestionsRef.current = currentIds;
  }, [suggestions]);

  // Separate suggestions with teaching guidance from regular suggestions
  const suggestionsWithGuidance = suggestions.filter(
    (s) => (s.suggestionsMetadata as any)?.teachingGuidance,
  );

  const regularSuggestions = suggestions.filter(
    (s) => !(s.suggestionsMetadata as any)?.teachingGuidance,
  );

  // Sort suggestions with conditional nudgeType priority
  const sortedSuggestionsWithGuidance = [...suggestionsWithGuidance].sort((a, b) => {
    const timeA = a.visibleForTutorAt ? new Date(a.visibleForTutorAt).getTime() : 0;
    const timeB = b.visibleForTutorAt ? new Date(b.visibleForTutorAt).getTime() : 0;

    // Check if times are within 1 minute
    const timeDiff = Math.abs(timeA - timeB);
    const oneMinute = 60 * 1000; // 1 minute in milliseconds

    if (timeDiff <= oneMinute) {
      // Within 1 minute - apply nudgeType priority
      const nudgeTypePriority = (type?: string) => {
        switch (type) {
          case 'personal':
            return 3;
          case 'combination':
            return 2;
          default:
            return 1;
        }
      };

      const aPriority = nudgeTypePriority((a.suggestionsMetadata as any)?.nudgeType);
      const bPriority = nudgeTypePriority((b.suggestionsMetadata as any)?.nudgeType);

      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
    }

    // Otherwise or if same nudgeType, sort by time (latest first)
    return timeB - timeA;
  });

  const sortedRegularSuggestions = [...regularSuggestions].sort((a, b) => {
    const timeA = a.visibleForTutorAt ? new Date(a.visibleForTutorAt).getTime() : 0;
    const timeB = b.visibleForTutorAt ? new Date(b.visibleForTutorAt).getTime() : 0;

    // Check if times are within 1 minute
    const timeDiff = Math.abs(timeA - timeB);
    const oneMinute = 60 * 1000;

    if (timeDiff <= oneMinute) {
      // Within 1 minute - apply nudgeType priority
      const nudgeTypePriority = (type?: string) => {
        switch (type) {
          case 'personal':
            return 3;
          case 'combination':
            return 2;
          default:
            return 1;
        }
      };

      const aPriority = nudgeTypePriority((a.suggestionsMetadata as any)?.nudgeType);
      const bPriority = nudgeTypePriority((b.suggestionsMetadata as any)?.nudgeType);

      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
    }

    // Otherwise or if same nudgeType, sort by time (latest first)
    return timeB - timeA;
  });

  if (!suggestions || suggestions.length === 0) {
    return (
      <div className='empty-state'>
        <p>Waiting for suggestions</p>
      </div>
    );
  }

  return (
    <div className='suggestions-list custom-scrollbar' key={`suggestions-${animationKey}`}>
      {/* Display suggestions with teaching guidance */}
      {sortedSuggestionsWithGuidance.map((suggestion, index) => (
        <TeachingGuidanceCard key={suggestion.id} suggestion={suggestion} index={index} />
      ))}

      {/* Display regular suggestions */}
      {sortedRegularSuggestions.map((suggestion, index) => (
        <SuggestionCard
          key={suggestion.id}
          suggestion={suggestion}
          index={sortedSuggestionsWithGuidance.length + index}
        />
      ))}
    </div>
  );
};

export default SuggestionPanel;
